import React from "react";
import { Input, Select, Spin } from "antd";
import { SearchOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { CustomIcons } from "../../assets";
import * as S from "./styles";

const { Option } = Select;

interface Company {
  id: string;
  companyName: string;
  companyLogo?: string;
  companyStatus: number;
}

interface CompaniesPanelProps {
  companiesData: Company[];
  isLoadingCompanies: boolean;
  searchValue: string;
  setSearchValue: (value: string) => void;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  totalCount: number;
  filteredCount: number;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onViewInvoices: (company: Company) => void;
}

const CompaniesPanel: React.FC<CompaniesPanelProps> = ({
  companiesData,
  isLoadingCompanies,
  searchValue,
  setSearchValue,
  statusFilter,
  setStatusFilter,
  totalCount,
  filteredCount,
  currentPage,
  totalPages,
  onPageChange,
  onViewInvoices,
}) => {
  return (
    <S.CompaniesPanel>
      <S.CompaniesPanelContent>
        <S.CompaniesPanelHeader>
          <S.PanelTitle>Companies</S.PanelTitle>
          <S.SearchFilterContainer>
            <Input
              placeholder="Search"
              allowClear
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{
                width: "388px",
                borderRadius: "10px",
                background: "#f8f8f8",
              }}
              prefix={
                <S.SearchIconWrapper>
                  <SearchOutlined />
                </S.SearchIconWrapper>
              }
            />
            <S.SelectWithPrefixIcon>
              <div className="prefix-icon">
                <CustomIcons.SortIcon
                  width={16}
                  height={16}
                  svgColor="rgba(0, 0, 0, 0.45)"
                />
              </div>
              <S.StyledSelect
                value={statusFilter}
                onChange={(value) => setStatusFilter(value as string)}
              >
                <Option value="all">Status</Option>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </S.StyledSelect>
            </S.SelectWithPrefixIcon>
          </S.SearchFilterContainer>
        </S.CompaniesPanelHeader>

        <S.ResultsText>
          Results: {statusFilter === "all" ? totalCount : filteredCount}
        </S.ResultsText>

        <S.CompaniesListContainer>
          {(() => {
            if (isLoadingCompanies) {
              return (
                <S.CenteredSpinner>
                  <Spin size="large" />
                </S.CenteredSpinner>
              );
            }

            if (companiesData.length === 0) {
              return (
                <S.CenteredEmpty>
                  <S.EmptyStateContainer>
                    <S.EmptyStateIconWrapper>
                      <CustomIcons.EmptyStateIcon
                        width={120}
                        height={120}
                        svgColor="#D0D5DD"
                      />
                    </S.EmptyStateIconWrapper>
                    <S.EmptyStateTitle>No Companies Found</S.EmptyStateTitle>
                    <S.EmptyStateDescription>
                      {searchValue || statusFilter !== "all"
                        ? "No companies match your search criteria"
                        : "There are no companies available at this time"}
                    </S.EmptyStateDescription>
                  </S.EmptyStateContainer>
                </S.CenteredEmpty>
              );
            }

            return (
              <S.CompaniesListContent>
                {companiesData.map((company) => {
                  // Swap: companyStatus === 1 displays as Inactive, companyStatus === 0 displays as Active
                  const isActive = company.companyStatus === 0;
                  const isSuspended = company.companyStatus === 1;

                  let statusText = "Unknown";
                  if (isActive) {
                    statusText = "Active";
                  } else if (isSuspended) {
                    statusText = "Suspended";
                  }

                  return (
                    <S.CompanyItem key={company.id}>
                      <S.CompanyInfo>
                        {company.companyLogo ? (
                          <S.CompanyLogo>
                            <img
                              src={company.companyLogo}
                              alt={company.companyName || ""}
                            />
                          </S.CompanyLogo>
                        ) : (
                          <S.CompanyLogo>
                            <CustomIcons.CompaniesIcon
                              width={24}
                              height={24}
                              viewBox={null}
                              svgColor="#9B9B9B"
                            />
                          </S.CompanyLogo>
                        )}
                        <S.CompanyDetails>
                          <S.CompanyName>
                            {company.companyName || "Unnamed Company"}
                          </S.CompanyName>
                          <S.StatusBadge
                            isActive={isActive}
                            isSuspended={isSuspended}
                          >
                            <S.StatusText
                              isActive={isActive}
                              isSuspended={isSuspended}
                            >
                              {statusText}
                            </S.StatusText>
                          </S.StatusBadge>
                        </S.CompanyDetails>
                      </S.CompanyInfo>
                      <S.ViewInvoicesButton
                        onClick={() => onViewInvoices(company)}
                      >
                        View Invoices
                      </S.ViewInvoicesButton>
                    </S.CompanyItem>
                  );
                })}
              </S.CompaniesListContent>
            );
          })()}
        </S.CompaniesListContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <S.PaginationContainer>
            <S.PaginationButton
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              isDisabled={currentPage === 1}
            >
              <LeftOutlined style={{ fontSize: "12px" }} />
              <S.PaginationButtonText>Back</S.PaginationButtonText>
            </S.PaginationButton>
            {(() => {
              const pages: number[] = [];
              const startPage = Math.max(1, currentPage - 2);
              const endPage = Math.min(totalPages, currentPage + 2);
              for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
              }
              return pages;
            })().map((page) => (
              <S.PaginationButton
                key={page}
                onClick={() => onPageChange(page)}
                isActive={currentPage === page}
              >
                <S.PaginationButtonText isActive={currentPage === page}>
                  {page}
                </S.PaginationButtonText>
              </S.PaginationButton>
            ))}
            <S.PaginationButton
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              isDisabled={currentPage >= totalPages}
            >
              <S.PaginationButtonText>Next</S.PaginationButtonText>
              <RightOutlined style={{ fontSize: "12px" }} />
            </S.PaginationButton>
          </S.PaginationContainer>
        )}
      </S.CompaniesPanelContent>
    </S.CompaniesPanel>
  );
};

export default CompaniesPanel;
